// @ts-nocheck
import { emptyGuid } from "@insite/client-framework/Common/StringHelpers";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    getCategoriesDataView,
    getCategoryState,
    getSubCategoryIds,
} from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import loadCategories from "@insite/client-framework/Store/Data/Categories/Handlers/LoadCategories";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
// import { BaseTheme } from "@insite/mobius/globals/baseTheme"; // Valtech Custom Code
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps, GridWidths } from "@insite/mobius/GridItem";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";

// Valtech Custom Code --->
import { ButtonPresentationProps } from "@example/MobiusOverrides/Button";
import { FoundationFieldDefinition } from "@store/Framework/Types/FoundationFieldDefinition";
import { TypographyPresentationProps } from "@store/MobiusOverrides/Typography/Typography";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import { getPageLinkByNodeId, LinkModel } from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { BaseTheme, ThemeTypography } from "@insite/mobius/globals/baseTheme";
import getColor from "@insite/mobius/utilities/getColor";
import fieldDefinitionBuilder from "@store/Framework/Common/Utilities/FieldDefinitionBuilder/fieldDefinitionBuilder";
import { FieldDefinitionBuilderOptions } from "@store/Framework/Types/FieldDefinitionBuilderTypes";
import BunzlStoreCategoryList, {
    CategoryResponsiveFieldsBreakpoints,
    defaultCategoryCardCss,
} from "@store/Library/Widgets/Category/BunzlStoreCategoryList";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import parse from "html-react-parser";
// <--- Valtech Custom Code

// bunzl custom code
import { carouselDefaultCustomCss } from "@store/Overrides/Components/Carousel";
import * as cssLinter from "css";
import { getCssUnitFieldDefinition } from "@store/Framework/Common/Fields/Toolkit/cssUnit";

// Shared Imports --->
import {
    CategoryListCommonFieldsProps,
    getSharedCategoryListFieldDefinitions,
} from "@store/Library/Widgets/Category/CategoryListFields";
// <--- Shared Imports

const enum fields {
    showOnlyTopLevelCategories = "showOnlyTopLevelCategories",
    overrideCategoryList = "overrideCategoryList",
    categoryLinks = "categoryLinks",
}

// Valtech Custom Code --->
export type CategoriesTiles = {
    [fields.overrideCategoryList]: boolean;
    [fields.categoryLinks]: LinkModel[];
};
// <--- Valtech Custom Code

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showOnlyTopLevelCategories]: boolean;
        [fields.overrideCategoryList]: boolean;
        [fields.categoryLinks]: LinkModel[];
    } & CategoriesTiles &
        FieldDefinitionBuilderOptions &
        CategoryListCommonFieldsProps; // Valtech Custom Code
}

const mapStateToProps = (state: ApplicationState, props: OwnProps) => {
    const getCategoriesParameter = {
        maxDepth: props.fields.showOnlyTopLevelCategories ? 1 : 2,
        includeStartCategory: false,
    };
    return {
        categoriesDataView: getCategoriesDataView(state, getCategoriesParameter),
        getCategoriesParameter,
        topLevelCategoryIds: getSubCategoryIds(state, emptyGuid),
    };
};

const mapDispatchToProps = {
    loadCategories,
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    ThemeProps<BaseTheme>;

export interface CategoryListStyles {
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    container?: GridContainerProps;
    categoryItem?: GridItemProps;
    innerContainer?: GridContainerProps;
    categoryImageItem?: GridItemProps;
    categoryImageLink?: LinkPresentationProps;
    categoryImage?: LazyImageProps;
    categoryNameLinkItem?: GridItemProps;
    categoryNameLink?: LinkPresentationProps;
    categoryName?: TypographyProps;
    subCategoryNameLinkItem?: GridItemProps;
    subCategoryNameLink?: LinkPresentationProps;
    viewMoreLinkItem?: GridItemProps;
    viewMoreLink?: LinkPresentationProps;
    // Valtech Custom Code
    categoryCard?: InjectableCss;
    categoryButton?: ButtonPresentationProps;
    categoryLinkName?: LinkPresentationProps;
    cardControlsWrapper?: InjectableCss;
    titleContentWrapper?: InjectableCss;
    title?: TypographyPresentationProps;
    content?: TypographyProps;
}

export const categoryListStyles: CategoryListStyles = {
    centeringWrapper: {
        css: css`
            height: ${pxToRem(300)};
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    container: {
        gap: 20,
    },
    categoryItem: {
        width: [12, 6, 4, 3, 3],
    },
    innerContainer: {
        css: css`
            width: 100%;
        `,
    },
    categoryImageItem: {
        css: css`
            height: ${pxToRem(160)};
        `,
    },
    categoryImage: {
        css: css`
            max-height: ${pxToRem(180)};
        `,
    },
    categoryNameLinkItem: {
        width: 12,
        css: css`
            padding-bottom: ${pxToRem(8)};
        `,
    },
    categoryLinkName: {
        color: "primary",
        css: css`
            &:hover {
                color: ${getColor("primary")};
                text-decoration: underline;
            }
        `,
        typographyProps: {
            color: "primary",
            forwardAs: "h4",
            variant: "h4",
            css: css`
                display: flex;
                align-items: center;
                text-align: center;
                margin: 0;
                min-height: ${pxToRem(88)};
                padding: 1em;
                ${({ theme }) => theme.design.typography.weight.base}
            `,
        },
    },
    subCategoryNameLinkItem: {
        width: 12,
    },
    viewMoreLinkItem: {
        width: 12,
        css: css`
            font-weight: bold;
            padding-top: ${pxToRem(10)};
        `,
    },
    categoryCard: {
        css: css`
            width: 100%;
            background-color: ${getColor("common.accentLighter")};
            border-bottom: ${pxToRem(5)} solid ${getColor("common.borderColored")};
        `,
    },
    categoryImageLink: {
        css: css`
            width: 100%;
            flex-direction: column;
        `,
    },
    categoryNameLink: {},
    categoryButton: {
        css: css`
            width: 100%;
            margin-bottom: 1.5em;
            padding: 0 ${pxToRem(14)};
        `,
    },
    cardControlsWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: center;
            width: 100%;
            padding: 0 1.5em;
        `,
    },
    titleContentWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: center;
            padding-bottom: 1em;
            margin-bottom: 2.5em;
            border-bottom: ${pxToRem(2)} solid ${getColor("common.borderLight")};
        `,
    },
    title: {
        color: "primary",
    },
    content: {
        css: css`
            max-width: ${pxToRem(950)};
        `,
    },
};

const styles = categoryListStyles;

class CategoryList extends React.Component<Props> {
    container = React.createRef<HTMLDivElement>();
    // Absolunet remove code : container, categoryItemStyles

    UNSAFE_componentWillMount(): void {
        const { categoriesDataView, getCategoriesParameter, loadCategories } = this.props;
        if (!categoriesDataView.isLoading && !categoriesDataView.value) {
            loadCategories(getCategoriesParameter);
        }
    }

    // Absolunet remove code : recalculateWidth, componentDidMount, componentWillUnmount

    render() {
        const {
            categoriesDataView,
            topLevelCategoryIds,

            fields: {
                // bunzl removed code showimages, showonlytoplevelcategories, displayfullimagetiles, showtitles, hideborders
                carouselOverflow,
                showArrows,
                title,
                titleType,
                content,
                numberOfItemsPerRow,
                responsive,
                gap,
                linkText,
                overrideCategoryList,
                categoryLinks,
                fromCategory,
                toCategory,
                /* Bunzl Custom Code */
                customCSS,
                titleStyle,
                showDots,
                useResponsiveOptions,
            }, // Valtech Custom Code
            fields,
        } = this.props;
        /* Bunzl Custom Code */
        const customCssWrapper = {
            css: css`
                ${customCSS}
            `,
        };
        if (categoriesDataView.isLoading) {
            return (
                <StyledWrapper {...styles.centeringWrapper}>
                    <LoadingSpinner {...styles.spinner} />
                </StyledWrapper>
            );
        }

        if (!categoriesDataView.value || !topLevelCategoryIds) {
            return null;
        }

        /* Foundation - Start */
        let selectedCategoryIds: string[] = [];
        if (overrideCategoryList && categoryLinks && categoryLinks?.length > 0) {
            selectedCategoryIds = categoryLinks.map(item => item.fields.destination.value);
        }

        let categories = overrideCategoryList
            ? categoriesDataView.value.filter(o => selectedCategoryIds.indexOf(o.id) > -1)
            : categoriesDataView.value.filter(o => topLevelCategoryIds.indexOf(o.id) > -1);

        // bunzl custom code
        categories = categories.filter(category => !(category.properties?.isExcludedFromList === "true"));
        if (!categories.length) {
            return null;
        }

        const titleStyles: TypographyProps = {
            ...styles.title,
            forwardAs: (titleType as keyof JSX.IntrinsicElements) || "h1",
            /* Bunzl Custom Code */
            variant: (titleStyle as keyof ThemeTypography) || "h1",
        };

        // <--- Valtech Custom Code

        return (
            // Bunzl Custom Code --->
            <StyledWrapper {...customCssWrapper}>
                {title || content ? (
                    <StyledWrapper {...styles.titleContentWrapper}>
                        {title && (
                            <Typography {...titleStyles} className="category-list-title">
                                {title}
                            </Typography>
                        )}
                        {content && (
                            <Typography className="category-list-content" {...styles.content}>
                                {parse(content, parserOptions)}
                            </Typography>
                        )}
                    </StyledWrapper>
                ) : null}
                <BunzlStoreCategoryList
                    linkText={linkText}
                    categories={categories}
                    carouselOverflow={carouselOverflow}
                    toCategory={toCategory}
                    fromCategory={fromCategory}
                    responsiveFields={responsive}
                    shouldUseResponsiveFields={useResponsiveOptions}
                    showArrows={showArrows}
                    numberOfItemsPerRow={numberOfItemsPerRow}
                    showDots={showDots}
                    gap={gap}
                />
            </StyledWrapper>
            // <--- Bunzl Custom Code
        );
    }
}

const linkMapStateToProps = (state: ApplicationState, ownProps: { categoryId: string }) => ({
    category: getCategoryState(state, ownProps.categoryId).value,
});

// Bunzl Custom Code--->
const widgetModule: WidgetModule<FoundationFieldDefinition> /* Valtech Custom Code */ = {
    component: connect(mapStateToProps, mapDispatchToProps)(withTheme(CategoryList)),
    definition: {
        group: "Categories",
        icon: "list",
        allowedContexts: ["CategoryListPage", "HomePage"],
        fieldDefinitions: [
            {
                name: fields.showOnlyTopLevelCategories,
                displayName: "Show Only Top Level Categories",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: -10,
            },
            {
                name: fields.overrideCategoryList,
                displayName: "Override Shown Category List?",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                variant: "toggle",
                fieldType: "General",
                sortOrder: 4,
            },
            {
                name: fields.categoryLinks,
                editorTemplate: "ListField",
                isVisible: item => item.fields.overrideCategoryList,
                getDisplay: (item: HasFields, state) => {
                    const {
                        destination: { type, value },
                    } = item.fields;
                    if (type === "Page") {
                        const link = getPageLinkByNodeId(state, value);
                        return link?.title;
                    }
                    if (type === "Category") {
                        let displayValue = "";
                        const categories = (state as any).pageEditor.categories;
                        if (!categories) {
                            displayValue = "Loading...";
                        } else {
                            const matchingCategories = categories.filter((o: { id: string }) => o.id === value);
                            if (matchingCategories.length === 0) {
                                displayValue = "Unknown";
                            } else {
                                displayValue = matchingCategories[0].shortDescription;
                            }
                        }

                        return displayValue;
                    }
                    return value;
                },
                defaultValue: [],
                fieldType: "Translatable", // Foundation
                fieldDefinitions: [
                    {
                        name: "destination",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Category",
                        },
                        allowUrls: item => false,
                        isRequired: true,
                    },
                ],
                sortOrder: 5,
            },
            ...getSharedCategoryListFieldDefinitions(),
        ],
    },
};

export default widgetModule;
